import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { useTenant } from '../../contexts/TenantContext'
import { useTeams } from '../../hooks/useTeams'
import { usePlayers } from '../../hooks/usePlayers'
import { Button, Input, Modal, Badge } from '../../components/ui'
import { 
  UserCircle, 
  Trash2, 
  ArrowLeft, 
  ArrowRight, 
  Sparkles, 
  AlertTriangle, 
  Target, 
  Loader2, 
  Award, 
  Zap, 
  Brain, 
  Heart, 
  Linkedin, 
  ShieldCheck, 
  CheckCircle2, 
  MessageSquare, 
  ExternalLink,
  ChevronRight,
  TrendingUp,
  Cpu,
  Trophy
} from 'lucide-react'
import { soundFx } from '../../lib/soundEffects'

export const EsportsScoutHQPage = () => {
  const navigate = useNavigate()
  const { tournamentId } = useParams()
  const { activeTenant } = useTenant()
  
  // Teams and Players hooks from tournament context
  const { teams, loading: loadingTeams } = useTeams(tournamentId)
  const { createPlayer } = usePlayers(tournamentId)

  // State for scout pipeline
  const [prospects, setProspects] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedProspect, setSelectedProspect] = useState(null)
  
  // Promotion modal state
  const [promotingProspect, setPromotingProspect] = useState(null)
  const [selectedTeamId, setSelectedTeamId] = useState('')
  const [newTeamName, setNewTeamName] = useState('')
  const [createAsNewTeam, setCreateAsNewTeam] = useState(false)
  const [isPromoting, setIsPromoting] = useState(false)

  useEffect(() => {
    fetchProspects()
  }, [])

  const fetchProspects = async () => {
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('esports_scout_pipeline')
        .select('*')
        .order('created_at', { ascending: false })
      
      if (error) throw error
      setProspects(data || [])
    } catch (err) {
      console.error('Error fetching prospects:', err)
    } finally {
      setLoading(false)
    }
  }

  // Move prospect to another lane
  const updateProspectLane = async (id, newLane) => {
    try {
      const { error } = await supabase
        .from('esports_scout_pipeline')
        .update({ lane: newLane })
        .eq('id', id)
      
      if (error) throw error
      
      soundFx.play('select')
      setProspects(prev => prev.map(p => p.id === id ? { ...p, lane: newLane } : p))
      
      // Update selected prospect state if open
      if (selectedProspect && selectedProspect.id === id) {
        setSelectedProspect(prev => ({ ...prev, lane: newLane }))
      }
    } catch (err) {
      alert('Fout bij het verplaatsen: ' + err.message)
    }
  }

  // Delete prospect
  const deleteProspect = async (id) => {
    if (!confirm('Weet je zeker dat je deze speler uit de pipeline wilt verwijderen?')) return
    try {
      const { error } = await supabase
        .from('esports_scout_pipeline')
        .delete()
        .eq('id', id)
      
      if (error) throw error
      
      soundFx.play('warning')
      setProspects(prev => prev.filter(p => p.id !== id))
      if (selectedProspect && selectedProspect.id === id) {
        setSelectedProspect(null)
      }
    } catch (err) {
      alert('Fout bij het verwijderen: ' + err.message)
    }
  }

  // Promote prospect to a tournament team
  const handlePromote = async (e) => {
    e.preventDefault()
    if (!promotingProspect) return
    
    setIsPromoting(true)
    try {
      let teamId = selectedTeamId
      
      // 1. Create team if needed
      if (createAsNewTeam) {
        if (!newTeamName.trim()) {
          alert('Vul een teamnaam in.')
          setIsPromoting(false)
          return
        }
        
        const { data: newTeam, error: teamError } = await supabase
          .from('teams')
          .insert([{
            name: newTeamName.trim(),
            tournament_id: tournamentId,
            tenant_id: activeTenant.id
          }])
          .select()
          .single()
          
        if (teamError) throw teamError
        teamId = newTeam.id
      }
      
      if (!teamId) {
        alert('Selecteer een team of maak een nieuw team aan.')
        setIsPromoting(false)
        return
      }

      // 2. Add player to the team
      await createPlayer({
        name: promotingProspect.name,
        team_id: teamId
      })

      // 3. Move prospect to 'hired' lane in database
      await supabase
        .from('esports_scout_pipeline')
        .update({ lane: 'hired' })
        .eq('id', promotingProspect.id)

      soundFx.play('success')
      alert(`🎉 ${promotingProspect.name} is succesvol toegevoegd aan het toernooi!`)
      
      // Refresh prospects
      fetchProspects()
      
      // Close modal
      setPromotingProspect(null)
      setSelectedTeamId('')
      setNewTeamName('')
      setCreateAsNewTeam(false)
    } catch (err) {
      alert('Promotie mislukt: ' + err.message)
    } finally {
      setIsPromoting(false)
    }
  }

  // Group prospects by lane
  const contactLane = prospects.filter(p => p.lane === 'contact')
  const scoutedLane = prospects.filter(p => p.lane === 'scouted')
  const trialLane = prospects.filter(p => p.lane === 'trial')
  const hiredLane = prospects.filter(p => p.lane === 'hired')

  // Calculated statistics
  const totalScouted = prospects.length
  const hiredCount = hiredLane.length
  const trialCount = trialLane.length
  const conversionRate = totalScouted > 0 ? Math.round((hiredCount / totalScouted) * 100) : 0

  return (
    <div className="min-h-screen bg-[#080610] text-slate-100 pb-16">
      <div className="premium-container animate-fadeIn">
        
        {/* Navigation back */}
        <div className="mb-6 animate-slideUp">
          <Button 
            variant="ghost" 
            onClick={() => navigate(`/admin/tournaments/${tournamentId}/teams`)} 
            className="text-slate-400 hover:text-white hover:bg-white/5"
          >
            <ArrowLeft size={16} className="mr-2" />
            Terug naar Teams
          </Button>
        </div>

        {/* Header Title Section */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 mb-10 animate-slideUp">
          <div className="flex items-center gap-4">
            <div className="p-4 rounded-2xl bg-gradient-to-br from-cyan-500 to-purple-600 shadow-[0_0_30px_rgba(168,85,247,0.35)] animate-pulse">
              <Target className="text-white" size={36} />
            </div>
            <div>
              <h1 className="text-4xl lg:text-5xl font-black tracking-tight uppercase italic text-white leading-none">
                ESPORTS <span className="text-cyan-400 drop-shadow-[0_0_8px_rgba(6,182,212,0.8)]">SCOUT HQ</span>
              </h1>
              <p className="text-slate-400 text-sm mt-1 uppercase tracking-widest font-mono">
                Real-time scouting console & AI Roster Builder
              </p>
            </div>
          </div>

          {/* Quick Info Badge */}
          <div className="flex items-center gap-2 bg-white/5 border border-white/10 px-4 py-2 rounded-xl backdrop-blur-md">
            <div className="w-2.5 h-2.5 bg-cyan-400 rounded-full animate-ping" />
            <span className="text-xs font-mono uppercase tracking-wider text-slate-300">Live Browser Scanner Koppeling Actief</span>
          </div>
        </div>

        {/* Live Scout Dashboard Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-10 animate-slideUp">
          
          <div className="bg-gradient-to-b from-[#181330] to-[#0d091e] border border-purple-500/20 p-5 rounded-2xl flex items-center justify-between shadow-[0_4px_20px_rgba(168,85,247,0.05)]">
            <div>
              <div className="text-[10px] text-slate-400 uppercase tracking-widest font-mono">Gescoute Spelers</div>
              <div className="text-3xl font-black text-white mt-1 font-sans">{totalScouted}</div>
            </div>
            <div className="p-3 bg-purple-500/10 text-purple-400 rounded-xl">
              <Cpu size={24} />
            </div>
          </div>

          <div className="bg-gradient-to-b from-[#181330] to-[#0d091e] border border-cyan-500/20 p-5 rounded-2xl flex items-center justify-between shadow-[0_4px_20px_rgba(6,182,212,0.05)]">
            <div>
              <div className="text-[10px] text-slate-400 uppercase tracking-widest font-mono">In Proefperiode</div>
              <div className="text-3xl font-black text-cyan-400 mt-1 font-sans">{trialCount}</div>
            </div>
            <div className="p-3 bg-cyan-500/10 text-cyan-400 rounded-xl">
              <TrendingUp size={24} />
            </div>
          </div>

          <div className="bg-gradient-to-b from-[#181330] to-[#0d091e] border border-emerald-500/20 p-5 rounded-2xl flex items-center justify-between shadow-[0_4px_20px_rgba(16,185,129,0.05)]">
            <div>
              <div className="text-[10px] text-slate-400 uppercase tracking-widest font-mono">Gecontracteerd (Hired)</div>
              <div className="text-3xl font-black text-emerald-400 mt-1 font-sans">{hiredCount}</div>
            </div>
            <div className="p-3 bg-emerald-500/10 text-emerald-400 rounded-xl">
              <Trophy size={24} />
            </div>
          </div>

          <div className="bg-gradient-to-b from-[#181330] to-[#0d091e] border border-amber-500/20 p-5 rounded-2xl flex items-center justify-between shadow-[0_4px_20px_rgba(245,158,11,0.05)]">
            <div>
              <div className="text-[10px] text-slate-400 uppercase tracking-widest font-mono">Conversie Ratio</div>
              <div className="text-3xl font-black text-amber-400 mt-1 font-sans">{conversionRate}%</div>
            </div>
            <div className="p-3 bg-amber-500/10 text-amber-400 rounded-xl">
              <Award size={24} />
            </div>
          </div>

        </div>

        {/* Kanban Board Container */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-32 bg-white/5 border border-white/10 rounded-3xl">
            <Loader2 className="animate-spin text-cyan-400 mb-4" size={40} />
            <p className="text-slate-400 font-mono text-sm uppercase tracking-widest animate-pulse">Syncen met scouting database...</p>
          </div>
        ) : prospects.length === 0 ? (
          <div className="text-center py-24 bg-white/5 border border-white/10 rounded-3xl p-8">
            <div className="p-5 rounded-2xl bg-white/5 w-20 h-20 flex items-center justify-center mx-auto mb-6 border border-white/10">
              <Target className="text-slate-600 animate-pulse" size={36} />
            </div>
            <h3 className="text-xl font-bold text-slate-300 mb-2">Geen esports talenten in de pipeline</h3>
            <p className="text-slate-500 max-w-md mx-auto text-sm leading-relaxed mb-6">
              Gebruik de **ProPoule Scout** Chrome-extensie om talenten rechtstreeks vanaf LinkedIn, Discord, Twitter of Liquipedia in deze database te schieten!
            </p>
            <div className="flex justify-center gap-3">
              <button 
                onClick={fetchProspects}
                className="bg-white/5 border border-white/10 px-4 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider hover:bg-white/10 transition-colors"
              >
                Pipeline Verversen
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-4 gap-6 items-start animate-slideUp">
            
            {/* Column 1: Contact */}
            <PipelineColumn 
              title="Gecontacteerd" 
              color="border-t-slate-500 text-slate-400"
              count={contactLane.length}
              prospects={contactLane}
              onSelect={setSelectedProspect}
              onMove={updateProspectLane}
              onDelete={deleteProspect}
              onPromote={setPromotingProspect}
              nextLane="scouted"
              nextLabel="Scout Speler"
            />

            {/* Column 2: Scouted */}
            <PipelineColumn 
              title="Scouted" 
              color="border-t-purple-500 text-purple-400"
              count={scoutedLane.length}
              prospects={scoutedLane}
              onSelect={setSelectedProspect}
              onMove={updateProspectLane}
              onDelete={deleteProspect}
              onPromote={setPromotingProspect}
              prevLane="contact"
              prevLabel="Zet in Contact"
              nextLane="trial"
              nextLabel="Start Proef"
            />

            {/* Column 3: Trial */}
            <PipelineColumn 
              title="Proefperiode" 
              color="border-t-cyan-500 text-cyan-400 animate-pulse"
              count={trialLane.length}
              prospects={trialLane}
              onSelect={setSelectedProspect}
              onMove={updateProspectLane}
              onDelete={deleteProspect}
              onPromote={setPromotingProspect}
              prevLane="scouted"
              prevLabel="Zet in Scouted"
              nextLane="hired"
              nextLabel="Contracteren"
            />

            {/* Column 4: Hired */}
            <PipelineColumn 
              title="Gecontracteerd" 
              color="border-t-emerald-500 text-emerald-400"
              count={hiredLane.length}
              prospects={hiredLane}
              onSelect={setSelectedProspect}
              onMove={updateProspectLane}
              onDelete={deleteProspect}
              onPromote={setPromotingProspect}
              prevLane="trial"
              prevLabel="Zet op Proef"
              isHiredColumn={true}
            />

          </div>
        )}

      </div>

      {/* Speler Detail AI Report Modal */}
      {selectedProspect && (
        <Modal 
          isOpen={true} 
          onClose={() => setSelectedProspect(null)} 
          title={`🤖 Scouting Profiel: ${selectedProspect.name}`}
          size="lg"
        >
          <div className="space-y-6 text-left text-slate-100 max-h-[80vh] overflow-y-auto pr-2 custom-scrollbar">
            
            {/* Header info */}
            <div className="flex items-center justify-between border-b border-white/10 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-cyan-500 to-purple-600 p-0.5 shadow-lg shadow-purple-500/20">
                  <div className="w-full h-full bg-[#080610] rounded-full flex items-center justify-center overflow-hidden">
                    <img 
                      src={selectedProspect.biometrics?.avatarUrl || `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(selectedProspect.name)}`} 
                      alt="Avatar" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-black text-white">{selectedProspect.name}</h3>
                  <p className="text-sm text-slate-400">{selectedProspect.role}</p>
                </div>
              </div>
              <div className="text-right">
                <span className={`text-[10px] px-2.5 py-1 rounded-full font-bold uppercase tracking-wider border ${
                  selectedProspect.platform === 'Discord' ? 'bg-indigo-500/10 border-indigo-500/20 text-indigo-400' :
                  selectedProspect.platform === 'LinkedIn' ? 'bg-blue-500/10 border-blue-500/20 text-blue-400' :
                  selectedProspect.platform === 'Twitter' ? 'bg-sky-500/10 border-sky-500/20 text-sky-400' : 'bg-pink-500/10 border-pink-500/20 text-pink-400'
                }`}>
                  {selectedProspect.platform}
                </span>
                <div className="text-[9px] text-slate-500 font-mono mt-1">{new Date(selectedProspect.created_at).toLocaleDateString()}</div>
              </div>
            </div>

            {/* Biometric Scores Panel */}
            {selectedProspect.biometrics && (
              <div className="bg-white/5 border border-white/10 p-5 rounded-2xl">
                <h4 className="text-xs font-black uppercase tracking-wider text-cyan-400 mb-4 flex items-center gap-1.5 font-mono">
                  <Cpu size={14} /> Biometrische Prestatie Scores
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                  <BiometricMeter label="Mechanical Skill" value={selectedProspect.biometrics.skill || 50} icon={<Award size={14} className="text-amber-400" />} />
                  <BiometricMeter label="Reaction Speed" value={selectedProspect.biometrics.speed || 50} icon={<Zap size={14} className="text-cyan-400" />} />
                  <BiometricMeter label="Game IQ (Brain)" value={selectedProspect.biometrics.brain || 50} icon={<Brain size={14} className="text-purple-400" />} />
                  <BiometricMeter label="Mental Focus" value={selectedProspect.biometrics.focus || 50} icon={<Target size={14} className="text-red-400" />} />
                  <BiometricMeter label="Team Chemistry" value={selectedProspect.biometrics.chemistry || 50} icon={<Heart size={14} className="text-pink-400" />} />
                </div>
              </div>
            )}

            {/* AI Scouting Report Panel */}
            <div className="bg-[#0f0b22] border border-purple-500/15 p-5 rounded-2xl">
              <h4 className="text-xs font-black uppercase tracking-wider text-purple-400 mb-3 flex items-center gap-1.5 font-mono">
                <Sparkles size={14} /> Google Gemini AI Scouting Analyse
              </h4>
              {selectedProspect.ai_report ? (
                <div className="text-sm leading-relaxed text-slate-300 whitespace-pre-wrap font-mono select-text selection:bg-purple-500 selection:text-white">
                  {selectedProspect.ai_report}
                </div>
              ) : (
                <div className="text-sm italic text-slate-500 py-2 flex items-center gap-2">
                  <AlertTriangle size={16} /> Geen AI rapport beschikbaar. Genereer er een via de browser extensie.
                </div>
              )}
            </div>

            {/* Action Bar inside modal */}
            <div className="flex flex-wrap gap-2 pt-4 border-t border-white/10">
              <Button 
                variant="ghost" 
                onClick={() => deleteProspect(selectedProspect.id)} 
                className="text-red-400 hover:bg-red-500/15"
              >
                <Trash2 size={16} className="mr-1.5" /> Pipeline Verwijderen
              </Button>
              <div className="flex-1" />
              {selectedProspect.lane !== 'hired' && (
                <Button 
                  variant="primary" 
                  onClick={() => {
                    setPromotingProspect(selectedProspect);
                    setSelectedProspect(null);
                  }}
                  className="bg-gradient-to-r from-emerald-600 to-green-600 shadow-md"
                >
                  <CheckCircle2 size={16} className="mr-1.5" /> Promoot naar Toernooi
                </Button>
              )}
            </div>

          </div>
        </Modal>
      )}

      {/* Promote / Hire Speler Modal */}
      {promotingProspect && (
        <Modal 
          isOpen={true} 
          onClose={() => {
            setPromotingProspect(null);
            setSelectedTeamId('');
            setNewTeamName('');
            setCreateAsNewTeam(false);
          }} 
          title={`🏆 Promoot Talent: ${promotingProspect.name}`}
          size="md"
        >
          <form onSubmit={handlePromote} className="space-y-6 text-left">
            <div className="bg-emerald-500/10 border border-emerald-500/20 p-4 rounded-xl text-emerald-400 text-xs leading-relaxed">
              Zet dit gescoute talent direct om in een actieve toernooispeler! Kies een bestaand team of maak een gloednieuw team aan.
            </div>

            {/* Toggle Existing vs New Team */}
            <div className="flex bg-white/5 p-1 rounded-xl border border-white/10">
              <button
                type="button"
                className={`flex-1 py-2 text-xs font-bold uppercase rounded-lg transition-all ${
                  !createAsNewTeam ? 'bg-emerald-600 text-white shadow' : 'text-slate-400 hover:text-white'
                }`}
                onClick={() => setCreateAsNewTeam(false)}
              >
                Bestaand Team
              </button>
              <button
                type="button"
                className={`flex-1 py-2 text-xs font-bold uppercase rounded-lg transition-all ${
                  createAsNewTeam ? 'bg-emerald-600 text-white shadow' : 'text-slate-400 hover:text-white'
                }`}
                onClick={() => setCreateAsNewTeam(true)}
              >
                Nieuw Team Aanmaken
              </button>
            </div>

            {!createAsNewTeam ? (
              <div>
                <label className="block text-xs font-black uppercase tracking-wider text-slate-300 mb-2">Kies Toernooi Team</label>
                {teams.length === 0 ? (
                  <div className="text-xs text-amber-400 italic bg-amber-500/5 p-3 rounded-lg border border-amber-500/10">
                    Er zijn nog geen teams in dit toernooi. Maak hiernaast een nieuw team aan!
                  </div>
                ) : (
                  <select
                    className="w-full px-4 py-3 rounded-xl border-2 border-white/10 bg-[#0d091e] text-slate-100 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all"
                    value={selectedTeamId}
                    onChange={(e) => setSelectedTeamId(e.target.value)}
                    required
                  >
                    <option value="">-- Selecteer een team --</option>
                    {teams.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                )}
              </div>
            ) : (
              <Input
                label="Nieuwe Teamnaam"
                placeholder="bijv. Team Liquid, FaZe Clan"
                value={newTeamName}
                onChange={(e) => setNewTeamName(e.target.value)}
                required
              />
            )}

            <div className="flex gap-3 pt-2">
              <Button 
                type="submit" 
                variant="primary" 
                className="flex-1 bg-gradient-to-r from-emerald-600 to-green-600"
                disabled={isPromoting}
              >
                {isPromoting ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="animate-spin" size={16} /> Bezig met toevoegen...
                  </span>
                ) : (
                  'Speler Toevoegen & Activeren'
                )}
              </Button>
              <Button 
                type="button" 
                variant="ghost" 
                onClick={() => setPromotingProspect(null)} 
                className="flex-1"
              >
                Annuleren
              </Button>
            </div>
          </form>
        </Modal>
      )}

    </div>
  )
}

// Subcomponent: Biometric Progress Bar Meter
const BiometricMeter = ({ label, value, icon }) => {
  return (
    <div className="flex flex-col items-center text-center p-3 rounded-xl bg-white/5 border border-white/5">
      <div className="flex items-center gap-1 mb-1">
        {icon}
        <span className="text-[10px] font-black text-slate-300 font-mono tracking-tight uppercase leading-none">{value}</span>
      </div>
      <div className="w-full bg-black/40 h-1.5 rounded-full overflow-hidden mb-1">
        <div className="bg-cyan-500 h-full rounded-full" style={{ width: `${value}%` }} />
      </div>
      <div className="text-[8px] text-slate-500 font-bold uppercase tracking-wider font-mono truncate max-w-full">{label}</div>
    </div>
  )
}

// Subcomponent: Kanban Board Column
const PipelineColumn = ({ 
  title, 
  color, 
  count, 
  prospects, 
  onSelect, 
  onMove, 
  onDelete, 
  onPromote,
  prevLane, 
  prevLabel, 
  nextLane, 
  nextLabel,
  isHiredColumn = false
}) => {
  return (
    <div className="flex flex-col bg-[#110e25]/60 border border-white/5 rounded-3xl min-h-[500px] h-[75vh]">
      
      {/* Column Header */}
      <div className={`p-4 border-b border-white/5 border-t-4 rounded-t-3xl ${color} flex items-center justify-between`}>
        <h3 className="font-black text-sm uppercase tracking-widest">{title}</h3>
        <Badge variant="secondary" className="bg-white/5 text-slate-300 font-mono">
          {count}
        </Badge>
      </div>

      {/* Prospect Cards Container */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar">
        {prospects.length === 0 ? (
          <div className="text-center py-12 text-slate-600 text-xs italic font-mono uppercase tracking-widest">
            Geen spelers
          </div>
        ) : (
          prospects.map(p => (
            <div 
              key={p.id}
              onClick={() => onSelect(p)}
              className="bg-white/5 border border-white/5 p-4 rounded-2xl hover:bg-white/10 hover:border-purple-500/20 hover:shadow-lg transition-all duration-300 group cursor-pointer text-left relative overflow-hidden"
            >
              {/* Profile Image & Meta */}
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-purple-600 p-0.5">
                  <div className="w-full h-full bg-[#0d091e] rounded-full overflow-hidden">
                    <img 
                      src={p.biometrics?.avatarUrl || `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(p.name)}`} 
                      alt="Avatar" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm truncate text-white group-hover:text-cyan-400 transition-colors">
                    {p.name}
                  </div>
                  <div className="text-slate-400 text-xs truncate">
                    {p.role}
                  </div>
                </div>
              </div>

              {/* Status bar */}
              {p.biometrics && (
                <div className="mt-3 flex items-center gap-3">
                  <div className="flex-1 flex gap-1 items-center">
                    <Zap size={10} className="text-cyan-400" />
                    <span className="text-[10px] text-slate-400 font-mono">
                      Skill: {p.biometrics.skill || 50}
                    </span>
                  </div>
                  <span className={`text-[8px] px-1.5 py-0.5 rounded font-black uppercase tracking-widest ${
                    p.platform === 'Discord' ? 'bg-indigo-500/20 text-indigo-400' :
                    p.platform === 'LinkedIn' ? 'bg-blue-500/20 text-blue-400' : 'bg-sky-500/20 text-sky-400'
                  }`}>
                    {p.platform}
                  </span>
                </div>
              )}

              {/* Card Actions Panel */}
              <div className="mt-4 flex items-center justify-between border-t border-white/5 pt-2 opacity-60 group-hover:opacity-100 transition-opacity" onClick={e => e.stopPropagation()}>
                
                {/* Delete button */}
                <button
                  onClick={() => onDelete(p.id)}
                  className="p-1.5 text-slate-500 hover:text-red-500 hover:bg-white/5 rounded-lg transition-colors"
                  title="Verwijder Prospect"
                >
                  <Trash2 size={13} />
                </button>

                <div className="flex gap-1">
                  {/* Transition Back */}
                  {prevLane && (
                    <button
                      onClick={() => onMove(p.id, prevLane)}
                      className="px-2 py-1 text-[9px] font-mono font-bold uppercase tracking-wider text-slate-500 hover:text-white bg-white/5 hover:bg-white/10 rounded"
                      title={prevLabel}
                    >
                      ←
                    </button>
                  )}

                  {/* Transition Next */}
                  {nextLane && (
                    <button
                      onClick={() => onMove(p.id, nextLane)}
                      className="px-2 py-1 text-[9px] font-mono font-bold uppercase tracking-wider text-cyan-400 hover:text-cyan-300 bg-cyan-500/5 hover:bg-cyan-500/10 rounded border border-cyan-500/10 hover:border-cyan-500/30"
                      title={nextLabel}
                    >
                      {nextLabel} →
                    </button>
                  )}

                  {/* Promote directly to tournament team */}
                  {!isHiredColumn && (
                    <button
                      onClick={() => onPromote(p)}
                      className="px-2 py-1 text-[9px] font-mono font-bold uppercase tracking-wider text-emerald-400 hover:text-emerald-300 bg-emerald-500/5 hover:bg-emerald-500/10 rounded border border-emerald-500/10 hover:border-emerald-500/30"
                      title="Promoveer naar toernooi"
                    >
                      Promoot 🏆
                    </button>
                  )}
                </div>

              </div>

            </div>
          ))
        )}
      </div>

    </div>
  )
}
